import { Icon } from '@iconify/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Tooltip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import constant from '../../../../utils/constants';
import BoxImage from '../../../../app/assets/images/delivery/Box.png';
import GoogleMap from '../../../../common/components/maps/googlemap/GoogleMap';
import CustomTextField from '../../../../common/components/customized/customtextfield/CustomTextField';
import GoogleTripDirections from '../../../../common/components/maps/googledirection/GoogleTripDirections';
import {
  deActivateLoad,
  deleteLoad,
  getLoadsAction,
  Load
} from '../../redux/reducer/deliveryTrackingSlices/addDeliveryLoadsSlice';
import './Loads.scss';
import { capitalizeFirstLetter, debounce } from '../../../../utils/commonFunctions';
import CustomMultiSelect from '../../../../common/components/customized/custommultiselect/CustomMultiSelect';
import CustomDeletePopup from '../../../../common/components/customdelete/CustomDeletePopup';
import CustomActivatePopup from '../../../../common/components/customactive/CustomActivatePopup';
import Custom3dContainer from '../3dModels/Custom3dContainer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import Custom3dLoad from '../3dModels/Custom3dLoad';

// Define interfaces
interface BoxData {
  boxId: number;
  barcodeId: string;
  boxDepth: number;
  boxWidth: number;
  boxHeight: number;
  boxWeight: number;
}

interface OrderData {
  orderId: number;
  assignedOrderId: string;
  customerOrderId: string | null;
  imageURL: string | null;
  deliveredTo: string | null;
  totalBoxesCount: number;
  orderStatus: string;
  orderType: string;
  deliveryBoxes: BoxData[];
  lat?: number;
  lng?: number;
}

interface LoadData {
  loadID: number;
  id: number;
  loadName: string;
  ordersCount: number;
  loadStatus: number;
  vehicleNumber: string | null;
  depth: number;
  width: number;
  height: number;
  boxWeight: number;
  loadConfigId?: number;
  isActive: number;
  ordersResponses: OrderData[];
}

interface LoadsProps {
  boolValue?: boolean;
  setChildState?: React.Dispatch<React.SetStateAction<any>>;
  children?: ReactNode;
}

interface SearchFormData {
  loadSearchField: string;
  orderField: string[];
}

const Loads = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: loads, isLoading, error } = useAppSelector(state => state.load);

  const [filteredRows, setFilteredRows] = useState<LoadData[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLoad, setSelectedLoad] = useState<LoadData | null>(null);
  const [view, setView] = useState<'map' | '3d'>('map');
  const [orderDropdown, setOrderDropdown] = useState<any>();
  const openMenu = Boolean(anchorEl);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [activateOpen, setActivateOpen] = useState(false);

  useEffect(() => {}, [loads]);

  const orderOption =
    selectedLoad &&
    selectedLoad?.ordersResponses?.length > 0 &&
    selectedLoad.ordersResponses.map((item: any) => ({
      id: item.assignedOrderId,
      label: item.assignedOrderId
    }));

  const { control, setValue, reset } = useForm<SearchFormData>({
    defaultValues: { loadSearchField: '', orderField: [] }
  });

  const handleClose = (e: any) => {
    e.stopPropagation();
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle selecting a load to view its orders
  const handleViewDetails = (load: LoadData) => {
    setSelectedLoad(load);
    setAnchorEl(null);
    const unique = Array.from(
      new Set(load.ordersResponses.map((item: any) => item.customerName))
    );

    setOrderDropdown(
      unique.map((item: any) => ({
        id: item,
        label: item
      }))
    );
    setValue('orderField', []);
    reset();
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/deliveryload-form', { state: null });
  };

  const handleSearch = useCallback(
    debounce((data: string) => {
      dispatch(
        getLoadsAction({
          pageNo: 1,
          pageSize: 500,
          search: data
        })
      );
    }),
    []
  );

  const handleFilterOrder = (params: any) => {
    const selectedIds = params.map((val: any) => val.id);
    const data = loads.find(
      (item: any, index: number) => item.loadName === selectedLoad?.loadName
    );
    setSelectedLoad((prev: any) => ({
      ...prev,
      ordersResponses:
        data?.ordersResponses && data.ordersResponses.length > 0
          ? data.ordersResponses.filter((item: any) =>
              selectedIds.includes(item.customerName)
            )
          : []
    }));
  };
  const handleDeleteLoadConfig = async () => {
    if (selectedLoad) {
      const response = await dispatch(
        deleteLoad({ id: Number(selectedLoad?.loadID), pageNo: 1, pageSize: 500 })
      );
      if (deleteLoad.fulfilled.match(response)) {
        await dispatch(getLoadsAction({ pageNo: 1, pageSize: 500, search: '' }));
        setOpen(false);
        setMessage('');
      }
    }
  };

  const handleDeactivateLoadConfig = async () => {
    if (selectedLoad) {
      const response = await dispatch(
        deActivateLoad({ id: Number(selectedLoad.id), pageNo: 1, pageSize: 500 })
      );
      if (deActivateLoad.fulfilled.match(response)) {
        await dispatch(getLoadsAction({ pageNo: 1, pageSize: 500, search: '' }));
        setActivateOpen(false);
        setMessage('');
      }
    }
  };

  // Map Redux state to local rows and update dynamicOptions
  useEffect(() => {
    if (loads) {
      setFilteredRows(
        loads.map((load: any) => ({
          ...load,
          id: load.id || 0,
          loadName: load.loadName || '',
          ordersCount: load.ordersCount || 0,
          loadStatus: load.loadStatus || 0,
          vehicleNumber: load.vehicleNumber || null,
          depth: parseFloat(load.depth) || 0,
          width: parseFloat(load.width) || 0,
          height: parseFloat(load.height) || 0,
          boxWeight: parseFloat(load.boxWeight) || 0,
          ordersResponses: (load.ordersResponses || []).map((order: any) => ({
            ...order,
            deliveryBoxes: order.deliveryBoxes || []
          }))
        }))
      );

      setSelectedLoad(loads.find((item: any, index: number) => index === 0));
      const unique = Array.from(
        new Set(loads?.[0]?.ordersResponses.map((item: any) => item.customerName))
      );
      setOrderDropdown(
        unique.map((item: any) => ({
          id: item,
          label: item
        }))
      );
    }
  }, [loads]);

  useEffect(() => {
    dispatch(getLoadsAction({ pageNo: 1, pageSize: 500, search: '' }));
  }, []);

  return (
    <Box className='loads-container'>
      <Box className='section-one'>
        <Typography variant='h6' className='section-one-text'>
          Loads
        </Typography>
        <Button
          variant='contained'
          endIcon={<Icon icon='material-symbols:add-rounded' width='16' height='16' />}
          className='section-one-button'
          onClick={handleBack}
        >
          Add Load
        </Button>
      </Box>

      <Grid container className='section-two-container' gap={1}>
        {/* First Grid: Loads */}
        <Grid item lg={3} md={4} sm={5} xs={12} className='section-two-grid'>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
            <CustomTextField
              id='loadSearchField'
              control={control}
              name='loadSearchField'
              placeholder='Search Loads...'
              icon={<SearchOutlinedIcon sx={{ color: 'grey' }} />}
              onChangeCallback={e => handleSearch(e)}
            />
          </Box>
          <Box sx={{ mt: 1, height: '70vh', overflowY: 'auto', scrollbarWidth: 'none' }}>
            {isLoading.getLoad ? (
              <Grid container flexDirection={'column'}>
                {[...Array(8)].map((_, index) => (
                  <Grid item xs={10} sm={5} lg={12} spacing={2} padding={'5px'}>
                    <Card
                      key={index}
                      sx={{ width: '100%', height: '80px', display: 'flex' }}
                    >
                      <Box height={'100px'} padding={'5px'}>
                        <Skeleton variant='rectangular' width={60} height={'70px'} />
                      </Box>
                      <Box>
                        <Skeleton variant='text' width={180} height={30} />
                        <Skeleton variant='text' width={180} height={20} />
                        <Box display={'flex'} justifyContent={'space-between'} gap={3}>
                          <Skeleton variant='text' width={80} height={20} />
                          <Skeleton variant='text' width={80} height={20} />
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : filteredRows.length < 0 ? (
              <Box
                component='img'
                src={BoxImage}
                alt='Loading'
                sx={{ width: 100, height: 100, mt: '20%', mx: 'auto', display: 'block' }}
              />
            ) : filteredRows.length > 0 ? (
              filteredRows.map(load => (
                <Card
                  key={load.id}
                  sx={{ position: 'relative', mt: 1, cursor: 'pointer' }}
                  onClick={() => handleViewDetails(load)}
                  className={`load-card ${
                    selectedLoad?.id === load.id ? 'selected' : ''
                  }`}
                >
                  <Grid container sx={{ display: 'flex' }}>
                    <Grid item xs={1.5}>
                      <Box sx={{ height: 40, mt: 1.5 }} fontSize={35}>
                        {'\u{1F4E6}'}
                      </Box>
                      {load?.isActive === 0 ? (
                        <Icon
                          icon='typcn:cancel'
                          width='18'
                          height='18'
                          style={{
                            position: 'absolute',
                            color: '#b50e0e',
                            left: '33px',
                            bottom: '12px'
                          }}
                        />
                      ) : (
                        <Icon
                          icon='nrk:check-active'
                          width='14'
                          height='14'
                          style={{
                            position: 'absolute',
                            color: 'green',
                            left: '35px',
                            bottom: '12px'
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={9.8}>
                      <Box sx={{ ml: 2 }}>
                        <Box sx={{ display: 'flex' }}>
                          <Typography className='load-id'>Load</Typography>
                          <Typography className='load-id' sx={{ ml: 1 }}>
                            :
                          </Typography>
                          <Tooltip title={capitalizeFirstLetter(load.loadName)}>
                            <Typography noWrap className='load-text'>
                              {capitalizeFirstLetter(load.loadName)}
                            </Typography>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: 'flex' }}>
                          <Typography className='load-id mt1'>Dimensions</Typography>
                          <Typography className='load-id' sx={{ ml: 1.5 }}>
                            :
                          </Typography>
                          <Tooltip
                            title={`${load.depth} x ${load.width} x ${load.height} mm`}
                          >
                            <Typography noWrap className='load-text mt1'>
                              {load.depth} x {load.width} x {load.height} mm
                            </Typography>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex' }}>
                            <Typography className='load-id mt1'>Orders</Typography>
                            <Typography className='load-id' sx={{ ml: 1.2 }}>
                              :
                            </Typography>
                            <Typography noWrap className='load-text mt1'>
                              {load.ordersCount}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex' }}>
                            <Typography className='load-id mt1'>Box Count</Typography>
                            <Typography className='load-id' sx={{ ml: 1.2 }}>
                              :
                            </Typography>
                            <Typography noWrap className='load-text mt1'>
                              {load.ordersResponses.reduce(
                                (sum: number, item: any) => sum + item.totalBoxesCount,
                                0
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid xs={0.5}>
                      <Icon
                        icon='majesticons:more-menu-vertical'
                        width='24'
                        height='24'
                        id='basic-button'
                        aria-controls='true'
                        onClick={(event: any) => {
                          handleClick(event);
                          setSelectedLoad(load);
                          event.stopPropagation();
                        }}
                      />
                    </Grid>

                    <Menu
                      id='basic-menu'
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleClose}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button'
                      }}
                    >
                      <>
                        <MenuItem
                          className='menu-dialog'
                          onClick={e => {
                            navigate('/deliveryload-form', { state: { selectedLoad } });
                          }}
                          disabled={selectedLoad?.isActive === 1 ? false : true}
                        >
                          <Icon icon='ic:baseline-edit' className='menu-icon update' />
                          {constant.Update}
                        </MenuItem>
                        <MenuItem
                          className='menu-dialog'
                          onClick={() => {
                            handleMenuClose();
                            setActivateOpen(true);
                          }}
                        >
                          {selectedLoad?.isActive === 0 ? (
                            <Icon
                              icon='hugeicons:tick-01'
                              color='green'
                              className='menu-icon'
                            />
                          ) : (
                            <Icon icon='gg:block' color='black' className='menu-icon' />
                          )}
                          {selectedLoad?.isActive === 0
                            ? constant.ActiveStatus
                            : constant.Deactive}
                        </MenuItem>
                        <MenuItem
                          className='menu-dialog'
                          onClick={event => {
                            handleMenuClose();
                            setOpen(true);
                            setMessage('Are you sure want to Delete the Load');
                            event.stopPropagation();
                          }}
                          disabled={selectedLoad?.isActive === 1 ? false : true}
                        >
                          <Icon
                            icon='ic:outline-delete'
                            className='menu-icon'
                            style={{ color: '#FF4343' }}
                          />
                          {constant.Delete}
                        </MenuItem>
                      </>
                    </Menu>
                  </Grid>
                </Card>
              ))
            ) : (
              <Box
                component='img'
                src={BoxImage}
                alt='No Loads'
                sx={{ width: 100, height: 100, mt: '20%', mx: 'auto', display: 'block' }}
              />
            )}
          </Box>
        </Grid>

        {/* Second Grid: Orders and Boxes */}
        <Grid item lg={4.5} md={7.8} sm={6.8} xs={12} className='section-two-grid'>
          <Box>
            <CustomMultiSelect
              id='orderField'
              options={orderDropdown || []}
              control={control}
              name='orderField'
              placeholder='Order ID'
              setValue={setValue}
              width='73%'
              onChanges={(e: any, newValue: any) => {
                if (newValue?.length === 0) {
                  setValue('orderField', []);
                  setSelectedLoad(prev =>
                    loads?.find((item: any) => item.loadName === prev?.loadName)
                  );
                } else {
                  handleFilterOrder(newValue);
                }
              }}
            />
            <Box
              sx={{ mt: 1, height: '70vh', overflowY: 'auto', scrollbarWidth: 'none' }}
            >
              {isLoading?.getLoad ? (
                <>
                  <Grid container>
                    {[...Array(8)].map((_, index) => (
                      <Grid item xs={10} sm={5} lg={6} spacing={2} padding={'5px'}>
                        <Card
                          key={index}
                          sx={{
                            width: '100%',
                            height: '100px',
                            display: 'flex',
                            padding: '5px'
                          }}
                        >
                          <Box>
                            <Skeleton variant='text' width={140} height={30} />
                            <Skeleton variant='text' width={140} height={20} />
                            <Skeleton variant='text' width={140} height={20} />
                            <Skeleton variant='text' width={140} height={20} />
                          </Box>
                          <Box height={'100px'} padding={'5px'}>
                            <Skeleton variant='rectangular' width={60} height={'50px'} />
                            <Skeleton
                              variant='rounded'
                              width={60}
                              height={'20px'}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : selectedLoad && selectedLoad.ordersResponses.length > 0 ? (
                <Grid container spacing={1}>
                  {selectedLoad.ordersResponses.map((order: any) => (
                    <Grid item xs={6} sm={6} md={6} lg={6}>
                      <Box
                        // elevation={isChecked ? 3 : 1}
                        className='order-item'
                        // onClick={() =>
                        //   toggleCheck(orderItem, orderItem?.selected ? false : true)
                        // }
                      >
                        {/* Order Info */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.4,
                            flex: 1,
                            minWidth: 0,
                            pl: 1.5
                          }}
                        >
                          <Typography
                            variant='subtitle2'
                            fontWeight={600}
                            noWrap
                            sx={{ fontSize: '0.85rem' }}
                          >
                            {order.customerName}
                          </Typography>

                          <Box
                            display='flex'
                            alignItems='center'
                            gap={0.5}
                            color='text.secondary'
                          >
                            <Icon
                              icon='lsicon:order-edit-outline'
                              width='18'
                              color='#1976d2'
                              height='18'
                            />
                            <Typography fontSize={11} noWrap>
                              {order.assignedOrderId}
                            </Typography>
                          </Box>

                          <Box
                            display='flex'
                            alignItems='center'
                            gap={0.5}
                            color='text.secondary'
                          >
                            <CalendarMonthIcon
                              fontSize='small'
                              sx={{ fontSize: 16, color: 'primary.main' }}
                            />
                            <Typography fontSize={11} noWrap>
                              {order.deliveryDate}
                            </Typography>
                          </Box>

                          <Box
                            display='flex'
                            alignItems='center'
                            // gap={0.5}
                            color='text.secondary'
                          >
                            <Inventory2Icon
                              fontSize='small'
                              sx={{ fontSize: 16, color: 'primary.main' }}
                            />
                            <Typography noWrap className='load-text mt1'>
                              {order.totalBoxesCount} boxes
                            </Typography>
                          </Box>
                        </Box>

                        {/* Icon + Status */}
                        <Box
                          sx={{
                            width: 60,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            flexShrink: 0
                          }}
                        >
                          <Box className='order-icon-container'>
                            <Typography fontSize={32}>{'\uD83D\uDCE6'}</Typography>
                          </Box>

                          <Chip
                            label={order.orderStatus}
                            size='small'
                            variant='outlined'
                            className={
                              order.orderStatus === 'IN_TRANSIT'
                                ? 'intransit-icon'
                                : order.orderStatus === 'PENDING'
                                ? 'pending-icon'
                                : order.orderStatus === 'NOT_DELIVERED'
                                ? 'notdelivered-icon'
                                : order.orderStatus === 'CANCELLED'
                                ? 'cancelled-icon'
                                : order.orderStatus === 'STOP_COMPLETE'
                                ? 'delivered-icon'
                                : 'rescheduled-icon'
                            }
                            sx={{
                              fontSize: '0.51rem',
                              height: 18,
                              px: 0.8,
                              borderRadius: '999px',
                              minWidth: 70,
                              maxWidth: 93,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'center',
                              fontWeight: 500,
                              letterSpacing: 0.2,
                              marginRight: 3
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box className='no-loads'>
                  <Typography className='text'>
                    {selectedLoad
                      ? 'No Orders Found for this Load'
                      : 'Select a Load to View Orders'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Third Grid: Map and 3D Preview */}
        <Grid item xs={12} sm={12} lg={4.3} className='section-two-grid'>
          <Box
            mb={1}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow:
                '0px 1px 3px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)'
            }}
          >
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, newView) => {
                if (newView) {
                  setView(newView);
                }
              }}
              fullWidth
              size='small'
              sx={{
                bgcolor: '#f1f5f9',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: 'none',
                  borderRadius: 0,
                  color: '#64748b',
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    color: '#fff',
                    bgcolor: '#1565c0',
                    '&:hover': {
                      bgcolor: '#0284c7'
                    }
                  },
                  '&:not(.Mui-selected):hover': {
                    bgcolor: '#e2e8f0'
                  },
                  fontWeight: 500
                }
              }}
            >
              <ToggleButton value='map'>Map</ToggleButton>
              <ToggleButton value='3d'>3D View</ToggleButton>
            </ToggleButtonGroup>

            {view === 'map' && (
              <Paper
                sx={{
                  p: 0,
                  pb: { xs: 2, md: 2, lg: 0 },
                  textAlign: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '77vh',
                  width: '100%',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8
                }}
              >
                <GoogleMap>
                  {selectedLoad && selectedLoad?.ordersResponses?.length > 0 && (
                    <GoogleTripDirections
                      locations={selectedLoad.ordersResponses?.map((item: any) => ({
                        lat: item.deliverableLat,
                        lng: item.deliverableLng,
                        name: 'trip',
                        info: { Mylocation: `Box ID: ${item?.orderId} ` || '' }
                      }))}
                    />
                  )}
                </GoogleMap>
              </Paper>
            )}

            {view === '3d' && selectedLoad && (
              <Paper
                sx={{
                  width: 'auto',
                  height: '77vh',
                  p: 0,
                  border: 'none',
                  borderRadius: 0,
                  overflow: 'hidden',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8
                }}
              >
                <Custom3dLoad
                  loads={selectedLoad.ordersResponses.flatMap(item =>
                    item.deliveryBoxes.map((item1: any) => ({
                      width: item1.boxWidth,
                      height: item1.boxHeight,
                      depth: item1.boxDepth,
                      x: item1.x,
                      y: item1.y,
                      z: item1.z,
                      color: Math.floor(Math.random() * 0xffffff)
                    }))
                  )}
                  container={{
                    width: selectedLoad?.width,
                    height: selectedLoad?.height,
                    depth: selectedLoad?.depth
                  }}
                />
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>

      {open && (
        <CustomDeletePopup
          open={open}
          setOpen={setOpen}
          handleClose={() => setOpen(false)}
          message={message}
          handleDelete={handleDeleteLoadConfig}
          isLoading={isLoading?.deleteLoad}
        />
      )}

      {activateOpen && (
        <CustomActivatePopup
          open={activateOpen}
          setOpen={setActivateOpen}
          filteredRow={selectedLoad}
          handleDeactivate={handleDeactivateLoadConfig}
          isLoading={isLoading?.deactivateLoad}
        />
      )}
    </Box>
  );
};

export default Loads;
